import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, KeyRound, Loader2, LogOut, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { useAuth, authFetch } from "@/context/AuthContext";
import { EnquiryRow } from "@/components/admin/EnquiryRow";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";

const STATUSES = ["all", "new", "contacted", "quoted", "closed"];

export default function AdminInbox() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (search) params.set("q", search);
    try {
      const [list, st] = await Promise.all([
        authFetch(`/api/admin/enquiries?${params.toString()}`),
        authFetch("/api/admin/enquiries/stats"),
      ]);
      setItems(list);
      setStats(st);
    } catch (e) {
      toast.error(e.message);
      if (/sign in|session|authenticated/i.test(e.message)) {
        await logout();
        navigate("/admin/login");
      }
    }
  }, [status, search, logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const onUpdated = (updated) => setItems((list) => list.map((i) => (i.id === updated.id ? updated : i)));
  const onDeleted = (id) => setItems((list) => list.filter((i) => i.id !== id));

  return (
    <div data-testid="admin-inbox-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-14">
      <Seo title="Enquiry Inbox" description="Private enquiry inbox for My Geeks LLC staff." />

      <div className="flex flex-col gap-6 border-b border-slate-200 pb-8 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="eyebrow flex items-center gap-2"><Inbox size={14} /> Private — Staff Only</p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Enquiry Inbox</h1>
          <p className="mt-2 text-sm text-slate-600">Signed in as <span data-testid="admin-user-email" className="font-semibold text-ink">{user?.email}</span></p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" data-testid="admin-refresh-button" onClick={load} className="btn-outline px-4 py-2.5 text-sm"><RefreshCw size={14} /> Refresh</button>
          <button type="button" data-testid="admin-change-password-button" onClick={() => setShowPw(true)} className="btn-outline px-4 py-2.5 text-sm"><KeyRound size={14} /> Change Password</button>
          <button type="button" data-testid="admin-logout-button" onClick={async () => { await logout(); navigate("/admin/login"); }} className="btn-secondary px-4 py-2.5 text-sm"><LogOut size={14} /> Sign Out</button>
        </div>
      </div>

      {stats && (
        <div data-testid="admin-stats" className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-5">
          {["total", "new", "contacted", "quoted", "closed"].map((k) => (
            <div key={k} data-testid={`admin-stat-${k}`} className="card p-5">
              <p className="label-xs">{k}</p>
              <p className="mt-1 font-display text-3xl font-bold">{stats[k]}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div data-testid="admin-status-filters" className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`admin-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${status === s ? "bg-ink text-white" : "border border-slate-300 bg-white text-slate-700 hover:bg-cream"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
          <Search size={16} className="text-slate-400" />
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email, reference…" className="w-full bg-transparent py-2.5 text-sm placeholder:text-slate-400 focus:outline-none md:w-80" />
          <button type="submit" data-testid="admin-search-button" className="text-sm font-semibold text-brand">Search</button>
        </form>
      </div>

      <div className="mt-6">
        {items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <p data-testid="admin-empty-state" className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500">No enquiries match this view.</p>
        ) : (
          <ul data-testid="admin-enquiry-list" className="card divide-y divide-slate-200 overflow-hidden">
            {items.map((item) => <EnquiryRow key={item.id} item={item} onUpdated={onUpdated} onDeleted={onDeleted} />)}
          </ul>
        )}
      </div>

      <ChangePasswordDialog open={showPw} onClose={() => setShowPw(false)} />
    </div>
  );
}
